import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, CheckCircle, Clock, FileText } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Button from '../../components/ui/Button';
import renewalRequestService from '../../services/renewalRequestService';
import RenewalRequestStatisticsCard from './components/RenewalRequestStatisticsCard';
import RenewalRequestsTable from './components/RenewalRequestsTable';
import EscalationMonitorPanel from './components/EscalationMonitorPanel';
import NonComplianceCasesPanel from './components/NonComplianceCasesPanel';
import RenewalRequestDetailsModal from './components/RenewalRequestDetailsModal';

const RenewalRequestTracker = () => {
  // State
  const [renewalRequests, setRenewalRequests] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [nonComplianceCases, setNonComplianceCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Filters
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedEscalationStatus, setSelectedEscalationStatus] = useState('all');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  // Modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Global filter handler
  const handleGlobalFilterChange = (filters) => {
    console.log('Global filters changed:', filters);
  };

  useEffect(() => {
    loadData();

    // Subscribe to real-time updates
    const requestsChannel = renewalRequestService?.subscribeToRenewalRequests(() => {
      loadData();
    });

    const casesChannel = renewalRequestService?.subscribeToNonComplianceCases(() => {
      loadNonComplianceCases();
    });

    return () => {
      renewalRequestService?.unsubscribeFromRenewalRequests(requestsChannel);
      renewalRequestService?.unsubscribeFromNonComplianceCases(casesChannel);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedStatus, selectedEscalationStatus, showOverdueOnly]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [requestsData, statsData] = await Promise.all([
        renewalRequestService?.getAllRenewalRequests(),
        renewalRequestService?.getRenewalRequestStatistics()
      ]);

      setRenewalRequests(requestsData || []);
      setStatistics(statsData);

      await loadNonComplianceCases();
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load renewal request data');
    } finally {
      setLoading(false);
    }
  };

  const loadNonComplianceCases = async () => {
    try {
      const casesData = await renewalRequestService?.getNonComplianceCases({
        caseStatus: 'open'
      });
      setNonComplianceCases(casesData || []);
    } catch (err) {
      console.error('Error loading non-compliance cases:', err);
    }
  };

  const applyFilters = async () => {
    try {
      const filters = {};

      if (selectedStatus !== 'all') {
        filters.requestStatus = selectedStatus;
      }

      if (selectedEscalationStatus !== 'all') {
        filters.escalationStatus = selectedEscalationStatus;
      }

      if (showOverdueOnly) {
        filters.isOverdue = true;
      }

      const filteredData = await renewalRequestService?.getAllRenewalRequests(filters);
      setRenewalRequests(filteredData || []);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError(err?.message || 'Failed to apply filters');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    setSuccess('Data refreshed successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleGenerateRequests = async () => {
    try {
      setProcessing(true);
      setError('');

      const result = await renewalRequestService?.triggerAutoGenerateRequests();
      
      setSuccess(result?.message || 'Renewal requests generated successfully');
      setTimeout(() => setSuccess(''), 3000);

      await loadData();
    } catch (err) {
      console.error('Error generating requests:', err);
      setError(err?.message || 'Failed to generate renewal requests');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendNotifications = async () => {
    try {
      setProcessing(true);
      setError('');

      const result = await renewalRequestService?.triggerSendNotifications();
      
      setSuccess(result?.message || 'Notifications sent successfully');
      setTimeout(() => setSuccess(''), 3000);

      await loadData();
    } catch (err) {
      console.error('Error sending notifications:', err);
      setError(err?.message || 'Failed to send notifications');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckOverdue = async () => {
    try {
      setProcessing(true);
      setError('');

      const result = await renewalRequestService?.triggerCheckOverdue();
      
      setSuccess(result?.message || 'Overdue check completed successfully');
      setTimeout(() => setSuccess(''), 3000);

      await loadData();
    } catch (err) {
      console.error('Error checking overdue:', err);
      setError(err?.message || 'Failed to check overdue requests');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleAcknowledge = async (requestId) => {
    try {
      await renewalRequestService?.acknowledgeRenewalRequest(requestId, 'Current User');
      setSuccess('Renewal request acknowledged');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      console.error('Error acknowledging request:', err);
      setError(err?.message || 'Failed to acknowledge request');
    }
  };

  const handleStartSubmission = async (requestId) => {
    try {
      await renewalRequestService?.markSubmissionStarted(requestId);
      setSuccess('Submission process started');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      console.error('Error starting submission:', err);
      setError(err?.message || 'Failed to start submission');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Bell className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Renewal Request Tracker
                </h1>
                <p className="text-gray-600 mt-1">
                  Automated renewal notifications with submission tracking and manager escalation
                </p>
              </div>
            </div>
            <DataRefreshIndicator lastRefresh={new Date()} onRefresh={handleRefresh} />
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex flex-wrap gap-3">
          <Button
            onClick={handleGenerateRequests}
            disabled={processing}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <FileText className="h-4 w-4 mr-2" />
            Generate Requests
          </Button>
          <Button
            onClick={handleSendNotifications}
            disabled={processing}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Bell className="h-4 w-4 mr-2" />
            Send Notifications
          </Button>
          <Button
            onClick={handleCheckOverdue}
            disabled={processing}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Clock className="h-4 w-4 mr-2" />
            Check Overdue
          </Button>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="mb-8">
            <RenewalRequestStatisticsCard statistics={statistics} />
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Request Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="notified">Notified</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="in_progress">In Progress</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Escalation Status
              </label>
              <select
                value={selectedEscalationStatus}
                onChange={(e) => setSelectedEscalationStatus(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Escalations</option>
                <option value="none">No Escalation</option>
                <option value="manager_notified">Manager Notified</option>
                <option value="department_head_notified">Department Head</option>
                <option value="hr_escalated">HR Escalated</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOverdueOnly}
                  onChange={(e) => setShowOverdueOnly(e?.target?.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Show Overdue Only
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Renewal Requests Table */}
            <RenewalRequestsTable
              requests={renewalRequests}
              onViewDetails={handleViewDetails}
              onAcknowledge={handleAcknowledge}
              onStartSubmission={handleStartSubmission}
            />

            {/* Escalation Monitor */}
            <EscalationMonitorPanel
              requests={renewalRequests?.filter(r => r?.escalationStatus !== 'none')}
            />

            {/* Non-Compliance Cases */}
            {nonComplianceCases?.length > 0 && (
              <NonComplianceCasesPanel cases={nonComplianceCases} />
            )}
          </div>
        )}
      </main>

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <RenewalRequestDetailsModal
          request={selectedRequest}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

export default RenewalRequestTracker;